// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router';
import { Grid, Row, Col, Image, Button } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import UserSidebar from '../../components/sidebars/UserSidebar';
import HelpWrapper from '../../components/HelpWrapper';
import * as userCreators from '../../actions/users/userCreators';
import { getUserLocations } from '../../actions/users/currentUserCreators';
import { setCurrentOrganisationOrLocation } from '../../actions/organisations/activeOrganisationCreators';
import { clearCurrentOrganisation } from '../../actions/organisations/currentOrganisationCreators';
import { getOrganisations } from '../../actions/organisations/basicCreators';
import { clearCurrentLocation } from '../../actions/locations/currentLocationCreators';
import { getCustomers } from '../../actions/customers/basicCreators';
import UserTabSidebar from './UserTabSidebar';
import UserIcon from '../../images/user-icon.png';
import { getSetting } from '../../actions/settings/settingsCreators';
import type { Dispatch } from 'redux';

type Props = {
  params: {
    userId: number,
    organisationId: number,
    context: string
  },
  currentOrganisation: Object,
  currentLocation: Object,
  getUsers: (i?: number, a?: boolean, e?: boolean) => void,
  router: {
    push: (url: string) => void
  },
  clearCurrentUser: () => void,
  updateCurrentUser: (uid: number, oid?: number) => void,
  setCurrentOrganisationOrLocation: (
    oid: number,
    c?: string,
    s?: boolean
  ) => Promise<*>,
  getUserSettings: (oid: any) => void,
  getSetting: (s: string) => void,
  clearCurrentOrganisation: () => void,
  clearCurrentLocation: () => void,
  clearLocations: () => void,
  getCustomers: () => void,
  getAllocatedUserOrganisations: (u: any, o: any) => Promise<*>,
  getFilteredUsers: Function,
  users: any,
  children: Object,
  settings: any
};

class Users extends React.Component<Props> {
  componentDidMount() {
    const {
      getSetting,
      clearCurrentOrganisation,
      clearCurrentLocation,
      getCustomers,
      getAllocatedUserOrganisations
    } = this.props;
    const { userId, organisationId } = this.props.params;
    if (this.props.params.context !== 'global') {
      this.handleUserIdUpdate(userId, organisationId);
      this.handleOrgIdUpdate(organisationId);
      getSetting('userHelpPdf');
      getSetting('userHelpVideo');
    } else {
      clearCurrentOrganisation();
      clearCurrentLocation();
      getSetting('globalUserHelpPdf');
      getSetting('globalUserHelpVideo');
      getCustomers();
      getOrganisations();
      if (userId) {
        getAllocatedUserOrganisations(userId);
      }
    }
  }

  componentWillUnmount() {
    localStorage.removeItem('backUrl');
  }

  componentWillReceiveProps(nextProps: Props) {
    const {
      currentOrganisation,
      params,
      params: { context },
      getUsers,
      clearCurrentOrganisation,
      clearCurrentLocation,
      getAllocatedUserOrganisations,
      router
    } = this.props;
    // Update Users if currentOrganisation has changed
    const currentOrg = currentOrganisation;
    const newCurrentOrg = nextProps.currentOrganisation;
    if (currentOrg.id !== newCurrentOrg.id && context !== 'global') {
      getUsers(newCurrentOrg.id);
      // update url back to /users
      router.push(`/customer/${newCurrentOrg.id}/users`);
    }
    if (
      context !== nextProps.params.context &&
      nextProps.params.context === 'global'
    ) {
      getUsers();
      clearCurrentOrganisation();
      clearCurrentLocation();
      getAllocatedUserOrganisations(params.userId);
    }

    // Update Users if currentLocation has changed
    const currentLocation = this.props.currentLocation;
    const newCurrentLocation = nextProps.currentLocation;
    const newOrganisationId = newCurrentLocation.id;
    const newContext = nextProps.params.context;
    if (
      (newContext === 'sub-account' &&
        currentLocation.id !== newCurrentLocation.id) ||
      (this.props.params.context !== newContext && newContext === 'sub-account')
    ) {
      this.props.getUsers(newOrganisationId);
      // update url back to /users
      this.props.router.push(`/sub-account/${newOrganisationId}/users`);
    }

    const currentUserId = this.props.params.userId;
    const newCurrentUserId = nextProps.params.userId;

    // Switching from Sub-Account back to Customer
    if (this.props.params.context !== newContext && newContext === 'customer') {
      this.props.getUsers(newCurrentOrg.id);
    }

    // Sub-Account has changed.
    if (newCurrentUserId !== currentUserId) {
      this.handleUserIdUpdate(
        newCurrentUserId,
        nextProps.params.organisationId
      );
    }
  }

  handleUserIdUpdate = (newId: number | string, organisationId: ?number) => {
    if (!newId || newId === 'new') {
      this.props.clearCurrentUser();
      return;
    }
    if (this.props.params.context === 'global') {
      this.props.updateCurrentUser(parseInt(newId, 10));
      this.props.getUserSettings(organisationId);
      this.props.getAllocatedUserOrganisations(newId, organisationId);
    } else {
      this.props.updateCurrentUser(
        parseInt(newId, 10),
        parseInt(organisationId, 10)
      );
    }
  };

  handleOrgIdUpdate = newId => {
    const orgId = parseInt(newId, 10);
    const { context } = this.props.params;
    // Retry function if it fails.
    // This fixes bug when using as landing screen and data not present.
    this.props
      .setCurrentOrganisationOrLocation(orgId, context)
      .catch(reason => {
        setInterval(() => {
          this.props.setCurrentOrganisationOrLocation(orgId, context);
        }, 1000);
      });
    this.props.getUserSettings(orgId);
  };

  getCurrentOrganisationByContext = (): ?Object => {
    const { context } = this.props.params;
    switch (context) {
      default:
      case 'global':
        return undefined;
      case 'customer':
        return this.props.currentOrganisation;
      case 'sub-account':
        return this.props.currentLocation;
    }
  };

  render() {
    const {
      users,
      getUsers,
      getFilteredUsers,
      children,
      settings,
      params: { context, userId, organisationId }
    } = this.props;
    const currentComponent = children ? children.type.displayName : '';
    const currentOrgId = parseInt(organisationId, 10);
    const backUrl = localStorage.getItem('backUrl');
    const currentOrganisation = this.getCurrentOrganisationByContext();

    let pdfUrl;
    let videoUrl;

    if (context === 'global') {
      pdfUrl =
        settings && settings.globalUserHelpPdf
          ? settings.globalUserHelpPdf.value
          : undefined;
      videoUrl =
        settings && settings.globalUserHelpVideo
          ? settings.globalUserHelpVideo.value
          : undefined;
    } else {
      pdfUrl =
        settings && settings.userHelpPdf
          ? settings.userHelpPdf.value
          : undefined;
      videoUrl =
        settings && settings.userHelpVideo
          ? settings.userHelpVideo.value
          : undefined;
    }

    if (
      (currentOrganisation && currentOrganisation.id === currentOrgId) ||
      context === 'global'
    ) {
      return (
        <Grid fluid>
          <Helmet>
            <title>Manage Users | Campaign Targeter</title>
          </Helmet>
          <Row>
            <Col sm={3} className="sidebar-wrapper">
              <UserSidebar
                userId={userId}
                currentOrgId={organisationId}
                isNew={userId === 'new'}
                users={users}
                getUsers={getUsers}
                getFilteredUsers={getFilteredUsers}
                currentOrganisation={currentOrganisation}
                context={context}
              />
            </Col>
            <Col sm={9} className="industry-maincontent">
              <div>
                <Row>
                  <HelpWrapper
                    pdfUrl={pdfUrl}
                    videoUrl={videoUrl}
                    extraClass={this.props.children ? 'child-page-present' : ''}
                  />
                </Row>
                {!children && context !== 'global' && (
                  <div>
                    <Link
                      to={`/${context}/${currentOrgId}/users/invite`}
                      className="well-300"
                    >
                      <Image src={UserIcon} />
                      <Button bsStyle="primary">Create new User</Button>
                    </Link>
                  </div>
                )}
              </div>
              {backUrl && (
                <Link to={backUrl} className="back-link">
                  &#11104; Back to customer
                </Link>
              )}
              {children && (
                <Row className="industries-details">
                  <Col sm={4} lg={3} className="industries-sidebar">
                    <UserTabSidebar
                      {...this.props}
                      activeTab={currentComponent}
                    />
                  </Col>
                  <Col sm={8} lg={9} className="industries-maincontent">
                    {React.cloneElement(children, { ...this.props })}
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Grid>
      );
    } else {
      return null;
    }
  }
}

function mapStateToProps(state) {
  return {
    users: state.users,
    currentOrganisation: state.currentOrganisation,
    currentLocation: state.currentLocation,
    currentUser: state.currentUser,
    settings: state.settings,
    customers: state.customers
  };
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  return bindActionCreators(
    {
      ...userCreators,
      setCurrentOrganisationOrLocation,
      clearCurrentOrganisation,
      clearCurrentLocation,
      getSetting,
      getCustomers,
      getUserLocations,
      getOrganisations
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Users);



// WEBPACK FOOTER //
// ./src/containers/users/Users.js